import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, update, onValue, push } from "firebase/database"; // 🛠 Removed unused 'remove'
import "./AdminPaint.css";
import { createPortal } from "react-dom";

export default function AdminPaint({ fgNumber, nodeName, onBack }) {
  const db = getDatabase();

  const noteOptions = ["Rust", "Visible Dust", "Oily", "Other"];
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [pendingDelete, setPendingDelete] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [batchDeleteKeys, setBatchDeleteKeys] = useState([]);

  const [staffList, setStaffList] = useState([]);
  const [form, setForm] = useState({
    datePaint: "",
    timePaint: "",
    userName: "",
    note: "",
  });
  const [images, setImages] = useState({
    qrUrl: "",
    checklistUrl: "",
  });

  const [editRemark, setEditRemark] = useState("");
  const [batchList, setBatchList] = useState([]);

  // NEW REMARK State Tracking
  const [issue, setIssue] = useState({
    message: "",
    reportedBy: "",
    reportedTime: "",
    status: "open"
  }); // 🛠 Removed unused 'issueRef' from here

  const androidToISODate = (val) => {
    if (!val) return "";
    const parts = val.split("/");
    if (parts.length !== 3) return "";
    const [d, m, y] = parts.map((s) => s.padStart(2, "0"));
    return `${y}-${m}-${d}`;
  };

  const isoToAndroidDate = (val) => {
    if (!val) return "";
    const [y, m, d] = val.split("-");
    return `${d}/${m}/${y}`;
  };

  const androidTimeTo24 = (val) => {
    if (!val) return "";
    const [time, modifier] = val.split(" ");
    if (!time) return val;
    let [h, mm] = time.split(":");
    let hour = parseInt(h, 10);
    if (modifier?.toLowerCase() === "pm" && hour < 12) hour += 12;
    if (modifier?.toLowerCase() === "am" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${mm}`;
  };

  const time24ToAndroid = (val) => {
    if (!val) return "";
    const [h, mm] = val.split(":");
    let hour = parseInt(h, 10);
    const modifier = hour >= 12 ? "pm" : "am";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${mm} ${modifier}`;
  };

  useEffect(() => {
    if (!fgNumber || !nodeName) return;

    const nodeRef = ref(db, `${nodeName}/${fgNumber}`);
    const staffRef = ref(db, "Staff");

    get(nodeRef)
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.val();
          setForm({
            datePaint: data.datePaint ? androidToISODate(data.datePaint) : "",
            timePaint: data.timePaint ? androidTimeTo24(data.timePaint) : "",
            userName: data.userName || "",
            note: data.note || "",
          });
          setImages({
            qrUrl: data.qrImageUrl || "",
            checklistUrl: data.imageUrl || "",
          });

          if (data.note) {
            const arr = data.note.split(",").map((n) => n.trim()).filter(Boolean);
            setSelectedNotes(arr);
          }
          if (data.batch) {
            const batchArr = Object.keys(data.batch).map((key) => ({
              label: data.batch[key].batchLabel || "",
              quantity: data.batch[key].quantity || "",
              firebaseKey: key,
            }));
            setBatchList(batchArr);
          }
        } else {
          const now = new Date();
          const todayISO = now.toISOString().split("T")[0];
          const time24 = now.toTimeString().slice(0, 5);
          setForm((prev) => ({ ...prev, datePaint: todayISO, timePaint: time24 }));
        }
      })
      .catch((err) => console.error("Error fetching record:", err));

    get(staffRef)
      .then((snap) => {
        const staff = [];
        snap.forEach((child) => {
          const name = child.child("name").val();
          if (name) staff.push(name);
        });
        setStaffList(staff);
      })
      .catch((err) => console.error("Error fetching staff:", err));
  }, [fgNumber, nodeName, db]);

  useEffect(() => {
    if (pendingDelete) {
      setForm((prev) => ({ ...prev, note: "" }));
    } else {
      setForm((prev) => ({ ...prev, note: selectedNotes.join("\n") }));
    }
  }, [selectedNotes, pendingDelete]);

  useEffect(() => {
    if (zoomImage) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
  }, [zoomImage]);

  const toggleNote = (note) => {
    setPendingDelete(false);
    if (note === "Other") {
      if (selectedNotes.some((n) => n.startsWith("Other:") || n === "Other")) {
        setSelectedNotes((prev) => prev.filter((n) => !n.startsWith("Other:") && n !== "Other"));
        setOtherText("");
      } else setSelectedNotes((prev) => [...prev, "Other"]);
      return;
    }
    setSelectedNotes((prev) =>
      prev.includes(note) ? prev.filter((n) => n !== note) : [...prev, note]
    );
  };

  const handleOtherSubmit = (e) => {
    e.preventDefault();
    if (!otherText.trim()) return;
    setSelectedNotes((prev) => [
      ...prev.filter((n) => !n.startsWith("Other")),
      `Other: ${otherText.trim()}`,
    ]);
    setOtherText("");
  };

  const handleDeleteNote = () => {
    if (window.confirm("Are you sure you want to delete all notes?")) {
      setPendingDelete(true);
      setSelectedNotes([]);
    }
  };

  const handleImageClick = (src) => {
    if (src) setZoomImage(src);
  };

  const closeZoom = () => {
    setZoomImage(null);
  };

  // ----------------- Save -----------------
  const handleSave = async () => {
    if (!fgNumber || !nodeName) return;

    const rootRef = ref(db);
    const updates = {};
    const now = Date.now();
    const paintPath = `Paint/${fgNumber}`;

    // 1. Core Paint Fields
    if (form.datePaint) updates[`${paintPath}/datePaint`] = isoToAndroidDate(form.datePaint);
    if (form.timePaint) updates[`${paintPath}/timePaint`] = time24ToAndroid(form.timePaint);
    if (form.userName) updates[`${paintPath}/userName`] = form.userName;
    
    // Note Logic
    if (pendingDelete) {
      updates[`${paintPath}/note`] = null;
    } else if (selectedNotes.length) {
      const cleanNotes = selectedNotes.filter(n => n !== "Other" && Boolean(n));
      updates[`${paintPath}/note`] = cleanNotes.join(", ");
    }
    
    // Mandatory timestamp to trigger listeners
    updates[`${paintPath}/lastVerifiedAt`] = now;

    // 2. Issue Handling (Admin Remark)
    if (editRemark.trim()) {
      updates[`${paintPath}/issue/status`] = "closed";
      updates[`${paintPath}/issue/remark`] = editRemark.trim();
      updates[`${paintPath}/issue/resolvedBy`] = form.userName || "Admin";
      updates[`${paintPath}/issue/resolvedTime`] = now;
    }

    // 3. Progress Synchronization
    updates[`Progress/${fgNumber}/s3`] = true;

    // 4. Dynamic Batch Handling
    batchDeleteKeys.forEach(key => {
      updates[`${paintPath}/batch/${key}`] = null;
    });

    batchList.forEach((row) => {
      if (!row.label && !row.quantity) return;

      const batchData = {
        batchLabel: row.label,
        quantity: row.quantity
      };

      if (row.firebaseKey) {
        updates[`${paintPath}/batch/${row.firebaseKey}`] = batchData;
      } else {
        const newBatchKey = push(ref(db, `${paintPath}/batch`)).key;
        updates[`${paintPath}/batch/${newBatchKey}`] = batchData;
      }
    });

    try {
      await update(rootRef, updates);

      setBatchDeleteKeys([]);
      if (pendingDelete) {
        setForm((prev) => ({ ...prev, note: "" }));
        setSelectedNotes([]);
        setPendingDelete(false);
      }

      if (onBack) onBack();
    } catch (err) {
      console.error("Save failed:", err);
      alert(`Failed to save: ${err.message}`);
    }
  };

  const handleDeleteFG = async () => {
    if (!fgNumber) {
      alert("FG Number not found.");
      return;
    }

    setIsDeleting(true);

    const updates = {
      [`Paint/${fgNumber}`]: null,
      [`Progress/${fgNumber}/s3`]: null,
      [`Receive/${fgNumber}/locationTag`]: "Treat"
    };

    try {
      const rootRef = ref(db);
      await update(rootRef, updates);

      console.log(`All records for ${fgNumber} updated successfully.`);

      setTimeout(() => {
        onBack && onBack();
      }, 1000);

    } catch (err) {
      console.error("❌ Error during update:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const updateBatchRow = (index, field, value) => {
    setBatchList((prev) => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const deleteBatchRow = (index) => {
    setBatchList((prev) => {
      const row = prev[index];
      if (row.firebaseKey) setBatchDeleteKeys((keys) => [...keys, row.firebaseKey]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const addBatchRow = () => setBatchList((prev) => [...prev, { label: "", quantity: "" }]);

  // ------------------ Remark ------------------
  useEffect(() => {
    if (!fgNumber) return;

    const issueRef = ref(db, `Paint/${fgNumber}/issue`);
    const unsubscribe = onValue(issueRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setIssue(data);
        setEditRemark(data.remark || "");
      } else {
        setIssue({
          message: "",
          reportedBy: "",
          reportedTime: "",
          status: "open",
        });
        setEditRemark("");
      }
    });

    return () => unsubscribe();
  }, [fgNumber, db]); // ✅ Added 'db' to dependency array safely

  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="admin-paint-root">
      <div className="card">
        <h2 className="title">{nodeName} for {fgNumber}</h2>
        
        {issue?.message && (
          <div className="issue-card">
            <div className="issue-header">
              <div className="issue-title">⚠ Issue Reported</div>
              <span className={`issue-status ${issue.status}`}>
                {issue.status ? issue.status.toUpperCase() : "UNKNOWN"}
              </span>
            </div>

            <div className="issue-message">
              {issue.message || "No issue message"}
            </div>

            <div className="issue-meta">
              <span>👤 {issue.reportedBy || "-"}</span>
              <span>🕒 {formatTime(issue.reportedTime)}</span>
            </div>

            <div className="issue-divider"></div>

            {issue?.status === "closed" && (
              <div className="issue-resolved-info">
                Resolved by: {issue?.resolvedBy || "-"} | {formatTime(issue?.resolvedTime)}
              </div>
            )}

            <textarea
              className="remark-editor"
              value={editRemark}
              placeholder="Enter or edit admin remark"
              onChange={(e) => setEditRemark(e.target.value)}
            />
          </div>
        )}

        <button className="delete-fg-btn" onClick={handleDeleteFG} title="Delete FG">
          Delete Paint 🗑
        </button>

        <div className="images-row">
          <div className="image-box" onClick={() => handleImageClick(images.qrUrl)}>
            <img src={images.qrUrl} alt="QR" />
            <div className="image-label">QR Code</div>
          </div>
          <div className="image-box" onClick={() => handleImageClick(images.checklistUrl)}>
            <img src={images.checklistUrl} alt="Checklist" />
            <div className="image-label">Checklist</div>
          </div>
        </div>

        {zoomImage && createPortal(
          <div className="image-zoom-overlay" onClick={closeZoom}>
            <div className="image-zoom-content">
              <img src={zoomImage} alt="Zoomed" className="zoomed-image" />
            </div>
          </div>,
          document.body
        )}

        <div className="form">
          <label className="form-row">
            <span className="label">Current Note:</span>
            <textarea readOnly value={form.note} className="current-note" />
          </label>

          <label className="form-row">
            <span className="label">New Note:</span>
            <div className="notes-controls">
              <div className="note-options">
                {noteOptions.map((note, i) => (
                  <button
                    key={i}
                    className={`note-btn ${selectedNotes.some((n) =>
                      note === "Other" ? n.startsWith("Other:") || n === "Other" : n === note
                    ) ? "active" : ""}`}
                    onClick={() => toggleNote(note)}
                  >
                    {note}
                  </button>
                ))}
                <button className="delete-note-btn" onClick={handleDeleteNote}>
                  🗑 Delete Note
                </button>
              </div>

              {selectedNotes.includes("Other") && (
                <form onSubmit={handleOtherSubmit} className="other-input-row">
                  <input
                    type="text"
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    placeholder="Enter other note..."
                  />
                  <button type="submit" className="add-note-btn">Add</button>
                </form>
              )}
            </div>
          </label>

          <div className="date-time-row">
            <label className="label">Paint Date:</label>
            <input type="date" value={form.datePaint} onChange={(e) => setForm({ ...form, datePaint: e.target.value })} />
          </div>

          <div className="date-time-row">
            <label className="label">Paint Time:</label>
            <input type="time" value={form.timePaint} onChange={(e) => setForm({ ...form, timePaint: e.target.value })} />
          </div>

          <div className="batch-section">
            <h3>Batch List</h3>
            {batchList.map((row, idx) => (
              <div key={idx} className="batch-row">
                <input type="text" placeholder="Batch Label" value={row.label} onChange={(e) => updateBatchRow(idx, "label", e.target.value)} />
                <input type="text" placeholder="Quantity" value={row.quantity} onChange={(e) => updateBatchRow(idx, "quantity", e.target.value)} />
                <button type="button" onClick={() => deleteBatchRow(idx)}>🗑</button>
              </div>
            ))}
            <button type="button" onClick={addBatchRow}>➕ Add Batch</button>
          </div>

          <label className="form-row">
            <span className="label">User:</span>
            <select value={form.userName} onChange={(e) => setForm({ ...form, userName: e.target.value })}>
              <option value="">Select User</option>
              {staffList.map((s, idx) => <option key={idx} value={s}>{s}</option>)}
            </select>
          </label>

          <div className="actions">
            <button className="btn-back" onClick={() => onBack && onBack()}>⬅ Back</button>
            <button className="btn-save" onClick={handleSave}>💾 Save</button>
          </div>
        </div>
      </div>
      {isDeleting && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Deleting FG Number...</p>
        </div>
      )}
    </div>
  );
}